import React, { useState, useEffect, useRef } from 'react';
import { Box, Typography, Button, Chip, LinearProgress } from '@mui/material';
import { 
    AutoAwesome as SparkleIcon,
    Refresh as RefreshIcon,
    PlayArrow as PlayIcon,
    SkipNext as SkipIcon
} from '@mui/icons-material';

interface Student {
    student_id: string;
    student_name: string;
}

interface BlindBagCallingProps {
    students: Student[];
    onTearComplete?: (winner: Student) => void;
    isFullscreen?: boolean;
}

type TearStage = 'idle' | 'shaking' | 'tearing' | 'peeking' | 'revealed';

interface BagTheme {
    name: string;
    icon: string;
    topGradient: string;
    bodyGradient: string;
    foilHighlight: string;
    accentColor: string;
    tagline: string;
}

const BAG_THEMES: BagTheme[] = [
    {
        name: 'Túi Tím Huyền Bí',
        icon: '🔮',
        topGradient: 'linear-gradient(135deg, #7c3aed 0%, #a855f7 50%, #c084fc 100%)',
        bodyGradient: 'linear-gradient(165deg, #3b0764 0%, #581c87 30%, #7e22ce 65%, #ec4899 100%)',
        foilHighlight: 'rgba(236, 72, 153, 0.4)',
        accentColor: '#fbbf24',
        tagline: 'GALAXY MYSTERY'
    },
    {
        name: 'Túi Vàng Hoàng Kim',
        icon: '🌟',
        topGradient: 'linear-gradient(135deg, #d97706 0%, #f59e0b 50%, #fde047 100%)',
        bodyGradient: 'linear-gradient(165deg, #78350f 0%, #b45309 30%, #d97706 65%, #ef4444 100%)',
        foilHighlight: 'rgba(251, 191, 36, 0.4)',
        accentColor: '#ffffff',
        tagline: 'GOLDEN FORTUNE'
    },
    {
        name: 'Túi Xanh May Mắn',
        icon: '🍀',
        topGradient: 'linear-gradient(135deg, #059669 0%, #10b981 50%, #6ee7b7 100%)',
        bodyGradient: 'linear-gradient(165deg, #064e3b 0%, #047857 30%, #059669 65%, #06b6d4 100%)',
        foilHighlight: 'rgba(16, 185, 129, 0.4)',
        accentColor: '#fef08a',
        tagline: 'LUCKY EMERALD'
    }
];

// Synthesize suspenseful sound effects using native Web Audio API
const playSound = (type: 'shake' | 'tear' | 'peeking' | 'reveal') => {
    try {
        const AudioContextClass = window.AudioContext || (window as any).webkitAudioContext;
        if (!AudioContextClass) return;
        const ctx = new AudioContextClass();

        if (type === 'shake') {
            const osc = ctx.createOscillator();
            const gain = ctx.createGain();
            osc.type = 'sine';
            osc.frequency.setValueAtTime(90, ctx.currentTime);
            osc.frequency.exponentialRampToValueAtTime(35, ctx.currentTime + 0.18);
            gain.gain.setValueAtTime(0.35, ctx.currentTime);
            gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.18);
            osc.connect(gain);
            gain.connect(ctx.destination);
            osc.start();
            osc.stop(ctx.currentTime + 0.18);
        } else if (type === 'tear') {
            const bufferSize = Math.floor(ctx.sampleRate * 0.45);
            const buffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
            const data = buffer.getChannelData(0);
            for (let i = 0; i < bufferSize; i++) {
                data[i] = (Math.random() * 2 - 1) * (1 - i / bufferSize);
            }
            const noise = ctx.createBufferSource();
            noise.buffer = buffer;
            const filter = ctx.createBiquadFilter();
            filter.type = 'bandpass';
            filter.frequency.setValueAtTime(1400, ctx.currentTime);
            filter.frequency.linearRampToValueAtTime(3500, ctx.currentTime + 0.35);
            const gain = ctx.createGain();
            gain.gain.setValueAtTime(0.35, ctx.currentTime);
            gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.45);
            noise.connect(filter);
            filter.connect(gain);
            gain.connect(ctx.destination);
            noise.start();
            noise.stop(ctx.currentTime + 0.45);
        } else if (type === 'peeking') {
            const osc = ctx.createOscillator();
            const gain = ctx.createGain();
            osc.type = 'sine';
            osc.frequency.setValueAtTime(300, ctx.currentTime);
            osc.frequency.linearRampToValueAtTime(650, ctx.currentTime + 0.5);
            gain.gain.setValueAtTime(0.12, ctx.currentTime);
            gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.5);
            osc.connect(gain);
            gain.connect(ctx.destination);
            osc.start();
            osc.stop(ctx.currentTime + 0.5);
        } else if (type === 'reveal') {
            const notes = [523.25, 659.25, 783.99, 1046.50]; // C5, E5, G5, C6 fanfare
            notes.forEach((freq, idx) => {
                const osc = ctx.createOscillator();
                const gain = ctx.createGain();
                osc.type = 'triangle';
                osc.frequency.setValueAtTime(freq, ctx.currentTime + idx * 0.09);
                gain.gain.setValueAtTime(0.25, ctx.currentTime + idx * 0.09);
                gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + idx * 0.09 + 0.7);
                osc.connect(gain);
                gain.connect(ctx.destination);
                osc.start(ctx.currentTime + idx * 0.09);
                osc.stop(ctx.currentTime + idx * 0.09 + 0.7);
            });
        }
    } catch (e) {
        console.warn('Web Audio synthesis error:', e);
    }
};

const BlindBagCalling: React.FC<BlindBagCallingProps> = ({ students, onTearComplete, isFullscreen = false }) => {
    const [themeIndex, setThemeIndex] = useState(0);
    const [stage, setStage] = useState<TearStage>('idle');
    const [winner, setWinner] = useState<Student | null>(null);
    const [progressPercent, setProgressPercent] = useState(0);
    const canvasRef = useRef<HTMLCanvasElement | null>(null);
    const timeoutsRef = useRef<NodeJS.Timeout[]>([]);

    const theme = BAG_THEMES[themeIndex];

    // Perfectly balanced proportions: significantly larger for podium visibility while fitting strictly within viewport without scroll
    const bagWidth = isFullscreen ? 480 : 400;
    const bagHeight = isFullscreen ? 500 : 445;
    const flapHeight = isFullscreen ? 74 : 66;
    const pouchHeight = isFullscreen ? 426 : 379;
    const cardWidth = isFullscreen ? 430 : 365;
    const cardHeight = isFullscreen ? 450 : 400;
    const areaWidth = isFullscreen ? 500 : 430;
    const areaHeight = isFullscreen ? 520 : 455;
    const auraSize = isFullscreen ? 600 : 520;

    const clearAllTimeouts = () => {
        timeoutsRef.current.forEach(clearTimeout);
        timeoutsRef.current = [];
    };

    useEffect(() => {
        return () => clearAllTimeouts();
    }, []);

    // Confetti animation on reveal (scaled up for high-res classroom screens)
    useEffect(() => {
        if (stage !== 'revealed' || !canvasRef.current) return;
        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        canvas.width = canvas.parentElement?.clientWidth || 700;
        canvas.height = canvas.parentElement?.clientHeight || 700;

        const colors = ['#FF6B6B', '#4ECDC4', '#FFE66D', '#FF8ED4', '#A855F7', '#38BDF8', '#10B981', '#F59E0B'];
        const particles = Array.from({ length: 75 }, () => ({
            x: canvas.width / 2 + (Math.random() * 60 - 30),
            y: canvas.height / 2,
            vx: (Math.random() - 0.5) * 14,
            vy: -Math.random() * 10 - 5,
            size: Math.random() * 10 + 5,
            color: colors[Math.floor(Math.random() * colors.length)],
            rotation: Math.random() * 360,
            vRot: (Math.random() - 0.5) * 14,
            opacity: 1
        }));

        let animId: number;
        const render = () => {
            ctx.clearRect(0, 0, canvas.width, canvas.height);
            let alive = false;
            particles.forEach(p => {
                p.x += p.vx;
                p.y += p.vy;
                p.vy += 0.24; // gravity
                p.rotation += p.vRot;
                p.opacity -= 0.008;

                if (p.opacity > 0) {
                    alive = true;
                    ctx.save();
                    ctx.translate(p.x, p.y);
                    ctx.rotate((p.rotation * Math.PI) / 180);
                    ctx.globalAlpha = Math.max(0, p.opacity);
                    ctx.fillStyle = p.color;
                    ctx.fillRect(-p.size / 2, -p.size / 2, p.size, p.size * 0.6);
                    ctx.restore();
                }
            });

            if (alive) {
                animId = requestAnimationFrame(render);
            }
        };

        animId = requestAnimationFrame(render);
        return () => cancelAnimationFrame(animId);
    }, [stage]);

    // TTS Call on reveal
    const triggerTTS = (studentName: string) => {
        try {
            const baseURL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1';
            const url = `${baseURL}/utils/tts?text=${encodeURIComponent('Đã chọn ' + studentName)}&lang=vi`;
            const audio = new Audio(url);
            audio.play().catch(e => console.error('TTS error:', e));
        } catch (e) {
            console.error(e);
        }
    };

    // Suspenseful Progressive Tear Flow
    const handleStartTear = () => {
        if (students.length === 0 || stage !== 'idle') return;
        clearAllTimeouts();

        // 1. Pick winner
        const selected = students[Math.floor(Math.random() * students.length)];
        setWinner(selected);

        // 2. Stage 1: Shaking (1.2s)
        setStage('shaking');
        setProgressPercent(20);
        playSound('shake');

        const t1 = setTimeout(() => {
            playSound('shake');
        }, 400);

        const t2 = setTimeout(() => {
            playSound('shake');
        }, 800);

        // 3. Stage 2: Tearing the edge (1.4s)
        const t3 = setTimeout(() => {
            setStage('tearing');
            setProgressPercent(50);
            playSound('tear');
        }, 1200);

        // 4. Stage 3: Peeking & Clues (1.6s)
        const t4 = setTimeout(() => {
            setStage('peeking');
            setProgressPercent(80);
            playSound('peeking');
        }, 2600);

        // 5. Stage 4: Burst & Revealed!
        const t5 = setTimeout(() => {
            setStage('revealed');
            setProgressPercent(100);
            playSound('reveal');
            triggerTTS(selected.student_name);
            if (onTearComplete) {
                onTearComplete(selected);
            }
        }, 4200);

        timeoutsRef.current = [t1, t2, t3, t4, t5];
    };

    // Fast-forward / Reveal immediately if user is impatient
    const handleSkip = () => {
        if (stage === 'revealed' || !winner) return;
        clearAllTimeouts();
        setStage('revealed');
        setProgressPercent(100);
        playSound('reveal');
        triggerTTS(winner.student_name);
        if (onTearComplete) {
            onTearComplete(winner);
        }
    };

    const handleReset = () => {
        clearAllTimeouts();
        setStage('idle');
        setWinner(null);
        setProgressPercent(0);
    };

    // Format display initials
    const getInitials = (name: string) => {
        const parts = name.trim().split(' ');
        if (parts.length >= 2) {
            return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
        }
        return (name[0] || 'HS').toUpperCase();
    };

    return (
        <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center', 
            width: '100%', 
            position: 'relative', 
            userSelect: 'none',
            py: 0.5
        }}>
            {/* Confetti Overlay */}
            <canvas 
                ref={canvasRef} 
                style={{ 
                    position: 'absolute', 
                    top: 0, 
                    left: 0, 
                    width: '100%', 
                    height: '100%', 
                    pointerEvents: 'none', 
                    zIndex: 20 
                }} 
            />

            {/* Bag Theme Selector Chips */}
            <Box sx={{ 
                display: 'flex', 
                gap: 0.8, 
                mb: 0.8, 
                zIndex: 5, 
                alignItems: 'center',
                flexWrap: 'wrap',
                justifyContent: 'center'
            }}>
                {BAG_THEMES.map((t, idx) => (
                    <Chip 
                        key={idx}
                        label={`${t.icon} ${t.name}`}
                        size="small"
                        clickable={stage === 'idle'}
                        onClick={() => stage === 'idle' && setThemeIndex(idx)}
                        variant={themeIndex === idx ? 'filled' : 'outlined'}
                        sx={{ 
                            fontWeight: 700,
                            fontSize: '0.82rem',
                            py: 1.5,
                            px: 0.8,
                            borderRadius: 3,
                            bgcolor: themeIndex === idx ? '#1e293b' : '#fff',
                            color: themeIndex === idx ? '#fff' : '#334155',
                            borderColor: themeIndex === idx ? '#1e293b' : '#cbd5e1',
                            boxShadow: themeIndex === idx ? '0 2px 6px rgba(0,0,0,0.12)' : 'none',
                            '&:hover': { bgcolor: themeIndex === idx ? '#0f172a' : '#f8fafc' }
                        }}
                    />
                ))}
            </Box>

            {/* Progress Suspense Bar */}
            <Box sx={{ width: '94%', maxWidth: 460, mb: 0.6, zIndex: 5 }}>
                <LinearProgress 
                    variant="determinate" 
                    value={progressPercent} 
                    sx={{ 
                        height: 7, 
                        borderRadius: 4,
                        bgcolor: '#e2e8f0',
                        boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.1)',
                        '& .MuiLinearProgress-bar': {
                            background: theme.bodyGradient,
                            borderRadius: 4,
                            transition: 'transform 0.4s ease'
                        }
                    }} 
                />
                <Typography sx={{ 
                    display: 'block', 
                    textAlign: 'center', 
                    mt: 0.4, 
                    color: stage === 'revealed' ? '#047857' : '#475569', 
                    fontWeight: 800, 
                    fontSize: '0.92rem',
                    letterSpacing: 0.3
                }}>
                    {stage === 'idle' && '✨ Chạm vào túi hoặc bấm nút bên dưới để mở!'}
                    {stage === 'shaking' && '🎲 Đang lắc túi may mắn...'}
                    {stage === 'tearing' && '✂️ Đang rạch mép túi bí ẩn...'}
                    {stage === 'peeking' && '👀 Ai đang dần hé lộ?!'}
                    {stage === 'revealed' && '🎉 CHÚC MỪNG BẠN HỌC SINH ĐƯỢC CHỌN! 🎉'}
                </Typography>
            </Box>

            {/* Main Interactive Bag Area */}
            <Box sx={{ 
                position: 'relative', 
                width: areaWidth, 
                height: areaHeight, 
                display: 'flex', 
                flexDirection: 'column', 
                alignItems: 'center', 
                justifyContent: 'center',
                my: 0.2
            }}>
                {/* Golden Radial Aura in Peeking / Revealed */}
                {(stage === 'peeking' || stage === 'revealed') && (
                    <Box sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        width: auraSize,
                        height: auraSize,
                        borderRadius: '50%',
                        background: 'radial-gradient(circle, rgba(251, 191, 36, 0.4) 0%, rgba(236, 72, 153, 0.15) 50%, rgba(255,255,255,0) 70%)',
                        zIndex: 1,
                        animation: 'spin 12s linear infinite',
                        pointerEvents: 'none'
                    }} />
                )}

                {/* THE BLIND BAG CONTAINER */}
                <Box sx={{
                    position: 'relative',
                    width: bagWidth,
                    height: bagHeight,
                    maxWidth: '96vw',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    cursor: stage === 'idle' ? 'pointer' : 'default',
                    animation: stage === 'shaking' ? 'bagShake 0.15s ease-in-out infinite alternate' : 'none',
                    transition: 'transform 0.3s ease',
                    '&:hover': stage === 'idle' ? { transform: 'scale(1.02)' } : {},
                    zIndex: 3
                }} onClick={stage === 'idle' ? handleStartTear : undefined}>

                    {/* TOP TEAR FLAP */}
                    <Box sx={{
                        position: 'relative',
                        width: '100%',
                        height: flapHeight,
                        background: theme.topGradient,
                        clipPath: 'polygon(0% 14px, 4% 0px, 8% 14px, 12% 0px, 16% 14px, 20% 0px, 24% 14px, 28% 0px, 32% 14px, 36% 0px, 40% 14px, 44% 0px, 48% 14px, 52% 0px, 56% 14px, 60% 0px, 64% 14px, 68% 0px, 72% 14px, 76% 0px, 80% 14px, 84% 0px, 88% 14px, 92% 0px, 96% 14px, 100% 0px, 100% 100%, 0% 100%)',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.22)',
                        borderBottom: '2.5px dashed rgba(255,255,255,0.85)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        px: 2,
                        zIndex: 6,
                        transition: 'all 0.6s cubic-bezier(0.34, 1.56, 0.64, 1)',
                        transformOrigin: 'top left',
                        transform: (stage === 'peeking' || stage === 'revealed') 
                            ? 'translateY(-30px) rotate(-22deg) scale(0.92)' 
                            : 'translateY(0) rotate(0)',
                        opacity: (stage === 'revealed') ? 0.6 : 1
                    }}>
                        <Typography sx={{ 
                            color: '#fff', 
                            fontSize: isFullscreen ? '1.1rem' : '0.95rem', 
                            fontWeight: 900, 
                            letterSpacing: 1.5, 
                            pl: 0.5, 
                            textShadow: '0 2px 4px rgba(0,0,0,0.5)' 
                        }}>
                            ✂️ TEAR HERE (XÉ TẠI ĐÂY)
                        </Typography>
                        <SparkleIcon sx={{ color: theme.accentColor, fontSize: isFullscreen ? 26 : 22 }} />

                        {/* Laser cut line during tearing */}
                        {stage === 'tearing' && (
                            <Box sx={{
                                position: 'absolute',
                                bottom: -2,
                                left: 0,
                                height: 4,
                                background: '#f43f5e',
                                boxShadow: '0 0 12px #f43f5e, 0 0 24px #fbbf24',
                                animation: 'laserCut 1.2s ease-in-out forwards'
                            }} />
                        )}
                    </Box>

                    {/* HIDDEN STUDENT CARD (Rising out of bag) */}
                    <Box sx={{
                        position: 'absolute',
                        top: 20,
                        width: cardWidth,
                        height: cardHeight,
                        maxWidth: '94%',
                        background: '#ffffff',
                        borderRadius: 4,
                        boxShadow: '0 18px 45px rgba(0,0,0,0.28), 0 0 30px rgba(251, 191, 36, 0.25)',
                        border: '4px solid #fbbf24',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        p: 2,
                        zIndex: (stage === 'revealed') ? 10 : 2,
                        transition: 'all 0.8s cubic-bezier(0.34, 1.56, 0.64, 1)',
                        transform: stage === 'idle' || stage === 'shaking' || stage === 'tearing'
                            ? 'translateY(35px) scale(0.9)'
                            : stage === 'peeking'
                            ? 'translateY(-25px) scale(0.95)'
                            : 'translateY(-15px) scale(1.04)',
                        opacity: stage === 'idle' || stage === 'shaking' ? 0 : 1
                    }}>
                        {/* Avatar */}
                        <Box sx={{
                            width: isFullscreen ? 100 : 88,
                            height: isFullscreen ? 100 : 88,
                            borderRadius: '50%',
                            background: stage === 'revealed' ? theme.bodyGradient : '#e2e8f0',
                            color: '#fff',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: isFullscreen ? '2.8rem' : '2.3rem',
                            fontWeight: 900,
                            boxShadow: '0 6px 18px rgba(0,0,0,0.18)',
                            mb: 1.2,
                            border: '3px solid #fff',
                            filter: stage === 'peeking' ? 'blur(5px)' : 'none',
                            transition: 'filter 0.5s ease'
                        }}>
                            {winner ? (stage === 'peeking' ? '❓' : getInitials(winner.student_name)) : '?'}
                        </Box>

                        {/* Student Name */}
                        <Typography sx={{ 
                            fontWeight: 900, 
                            color: '#0f172a', 
                            textAlign: 'center',
                            fontSize: isFullscreen ? '2.6rem' : '2.15rem',
                            lineHeight: 1.2,
                            textTransform: 'uppercase',
                            letterSpacing: 0.8,
                            filter: stage === 'peeking' ? 'blur(6px)' : 'none',
                            transition: 'filter 0.5s ease',
                            px: 1,
                            wordBreak: 'break-word',
                            textShadow: stage === 'revealed' ? '0 1px 4px rgba(0,0,0,0.08)' : 'none'
                        }}>
                            {winner ? winner.student_name : 'Học sinh'}
                        </Typography>

                        {/* Subtitle / Badge */}
                        <Box sx={{ mt: 1.2 }}>
                            {stage === 'revealed' ? (
                                <Chip 
                                    label="🎉 ĐÃ ĐƯỢC CHỌN!" 
                                    color="success" 
                                    sx={{ 
                                        fontWeight: 900, 
                                        fontSize: '1.05rem', 
                                        py: 1.8, 
                                        px: 1.8, 
                                        borderRadius: 2.5,
                                        boxShadow: '0 3px 10px rgba(16, 185, 129, 0.25)'
                                    }} 
                                />
                            ) : (
                                <Typography sx={{ color: '#64748b', fontWeight: 700, fontSize: '0.95rem' }}>
                                    👀 Đang hé lộ...
                                </Typography>
                            )}
                        </Box>
                    </Box>

                    {/* LOWER POUCH BODY */}
                    <Box sx={{
                        position: 'relative',
                        width: '100%',
                        height: pouchHeight,
                        background: theme.bodyGradient,
                        clipPath: 'polygon(0% 0%, 100% 0%, 100% calc(100% - 14px), 96% 100%, 92% calc(100% - 14px), 88% 100%, 84% calc(100% - 14px), 80% 100%, 76% calc(100% - 14px), 72% 100%, 68% calc(100% - 14px), 64% 100%, 60% calc(100% - 14px), 56% 100%, 52% calc(100% - 14px), 48% 100%, 44% calc(100% - 14px), 40% 100%, 36% calc(100% - 14px), 32% 100%, 28% calc(100% - 14px), 24% 100%, 20% calc(100% - 14px), 16% 100%, 12% calc(100% - 14px), 8% 100%, 4% calc(100% - 14px), 0% 100%)',
                        boxShadow: '0 10px 28px rgba(0,0,0,0.3)',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        py: 2,
                        px: 2,
                        zIndex: 4,
                        overflow: 'hidden',
                        '&::before': {
                            content: '""',
                            position: 'absolute',
                            top: 0,
                            left: '-100%',
                            width: '200%',
                            height: '100%',
                            background: 'linear-gradient(60deg, transparent 35%, rgba(255,255,255,0.22) 50%, transparent 65%)',
                            animation: stage === 'idle' ? 'sheen 4s infinite' : 'none'
                        }
                    }}>
                        {/* Badge */}
                        <Box sx={{
                            bgcolor: 'rgba(255,255,255,0.18)',
                            backdropFilter: 'blur(5px)',
                            borderRadius: 6,
                            px: 2,
                            py: 0.35,
                            border: '1px solid rgba(255,255,255,0.35)',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.8
                        }}>
                            <Typography sx={{ color: '#fff', fontSize: '0.85rem', fontWeight: 900, letterSpacing: 1.5 }}>
                                {theme.tagline}
                            </Typography>
                        </Box>

                        {/* Center Mystery Icon */}
                        <Box sx={{ 
                            display: 'flex', 
                            flexDirection: 'column', 
                            alignItems: 'center',
                            my: 'auto'
                        }}>
                            <Typography sx={{ 
                                fontSize: isFullscreen ? '5.6rem' : '4.6rem', 
                                lineHeight: 1,
                                filter: 'drop-shadow(0 6px 12px rgba(0,0,0,0.3))',
                                animation: stage === 'shaking' ? 'pulse 0.4s infinite' : 'none'
                            }}>
                                {theme.icon}
                            </Typography>
                            <Typography sx={{ 
                                color: '#fff', 
                                fontWeight: 900, 
                                fontSize: isFullscreen ? '2.3rem' : '2.0rem', 
                                mt: 0.8,
                                letterSpacing: 2,
                                textShadow: '0 2px 6px rgba(0,0,0,0.5)'
                            }}>
                                TÚI MÙ
                            </Typography>
                            <Typography sx={{ 
                                color: theme.accentColor, 
                                fontWeight: 800, 
                                fontSize: '1rem', 
                                letterSpacing: 1.5, 
                                mt: 0.3
                            }}>
                                ★ BÍ MẬT HỌC SINH ★
                            </Typography>
                        </Box>

                        {/* Bottom Barcode / Deco */}
                        <Box sx={{ width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', opacity: 0.8, px: 0.5 }}>
                            <Typography sx={{ color: '#fff', fontSize: '0.82rem', fontFamily: 'monospace', fontWeight: 700 }}>
                                #LMS-{students.length}HS
                            </Typography>
                            <Box sx={{ display: 'flex', gap: '2px', height: 16 }}>
                                {[6, 14, 8, 16, 10, 4, 15, 9, 16, 7, 13, 5, 14].map((h, i) => (
                                    <Box key={i} sx={{ width: 2.5, height: h, bgcolor: '#fff', borderRadius: '1px' }} />
                                ))}
                            </Box>
                        </Box>
                    </Box>
                </Box>
            </Box>

            {/* Action Buttons */}
            <Box sx={{ display: 'flex', gap: 1.5, mt: 0.8, mb: 0, zIndex: 5, width: '100%', justifyContent: 'center' }}>
                {stage === 'idle' ? (
                    <Button 
                        variant="contained" 
                        size="large"
                        onClick={handleStartTear}
                        disabled={students.length === 0}
                        startIcon={<PlayIcon sx={{ fontSize: 26 }} />}
                        sx={{ 
                            background: theme.bodyGradient, 
                            color: '#fff', 
                            fontWeight: 900, 
                            fontSize: '1.18rem', 
                            borderRadius: 7, 
                            px: 4.8, 
                            py: 1.15,
                            boxShadow: '0 4px 18px rgba(0,0,0,0.22)',
                            textTransform: 'none',
                            letterSpacing: 0.5,
                            '&:hover': { opacity: 0.95, transform: 'scale(1.02)' },
                            transition: 'all 0.2s ease'
                        }}
                    >
                        ✂️ Bắt đầu xé túi!
                    </Button>
                ) : stage === 'revealed' ? (
                    <Button 
                        variant="contained" 
                        size="large"
                        onClick={handleReset}
                        startIcon={<RefreshIcon sx={{ fontSize: 24 }} />}
                        sx={{ 
                            bgcolor: '#1e293b', 
                            color: '#fff', 
                            fontWeight: 900, 
                            fontSize: '1.18rem', 
                            borderRadius: 7, 
                            px: 4.8, 
                            py: 1.15,
                            textTransform: 'none',
                            letterSpacing: 0.5,
                            boxShadow: '0 4px 15px rgba(0,0,0,0.2)',
                            '&:hover': { bgcolor: '#0f172a', transform: 'scale(1.02)' },
                            transition: 'all 0.2s ease'
                        }}
                    >
                        🔄 Xé túi tiếp theo
                    </Button>
                ) : (
                    <Button 
                        variant="outlined" 
                        size="medium"
                        onClick={handleSkip}
                        endIcon={<SkipIcon sx={{ fontSize: 20 }} />}
                        sx={{ 
                            borderColor: '#94a3b8', 
                            color: '#334155', 
                            borderRadius: 7, 
                            textTransform: 'none', 
                            fontWeight: 700, 
                            fontSize: '0.95rem',
                            px: 3,
                            py: 0.8,
                            bgcolor: '#fff',
                            '&:hover': { bgcolor: '#f8fafc', borderColor: '#64748b' }
                        }}
                    >
                        Xem kết quả ngay ⏩
                    </Button>
                )}
            </Box>

            {/* Keyframe animations */}
            <style>{`
                @keyframes bagShake {
                    0% { transform: rotate(-3deg) scale(1.02); }
                    100% { transform: rotate(3deg) scale(0.98); }
                }
                @keyframes laserCut {
                    0% { width: 0%; left: 0%; opacity: 1; }
                    80% { width: 100%; left: 0%; opacity: 1; }
                    100% { width: 100%; left: 0%; opacity: 0; }
                }
                @keyframes sheen {
                    0% { left: -100%; }
                    40%, 100% { left: 150%; }
                }
                @keyframes spin {
                    from { transform: translate(-50%, -50%) rotate(0deg); }
                    to { transform: translate(-50%, -50%) rotate(360deg); }
                }
            `}</style>
        </Box>
    );
};

export default BlindBagCalling;
